import React, {useState, useCallback, useMemo} from 'react';
import {getErrorMessage} from '@/utils/getMessages';
import {Text} from '../Text';
import {cn} from '@/utils/common_functions';
import {useToast} from '@/hooks';

interface FileUploadProps {
  label: string;
  required?: boolean;
  error?: string;
  touched?: boolean;
  acceptedTypes: string[];
  maxSizeInMB?: number;
  onFileSelect: (file: File | null) => void;
  selectedFile?: File | null;
  placeholder?: string;
  className?: string;
  onBlur?: (e?: any) => void; // 👈 add this
}

export const FileUpload: React.FC<FileUploadProps> = props => {
  const {label, required = false, error, touched = false, acceptedTypes, maxSizeInMB, onBlur, onFileSelect, selectedFile, placeholder, className} = props;
  const [dragOver, setDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const {showToast} = useToast();

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isValidType = acceptedTypes.some(type => type.toLowerCase() === fileExtension);

      if (!isValidType) {
        const errorCode = acceptedTypes.includes('pdf') ? 'E-10016' : 'E-10023';
        return getErrorMessage(errorCode);
      }

      // Check file size
      if (maxSizeInMB) {
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          return getErrorMessage('E-10013');
        }
      }

      return null;
    },
    [acceptedTypes, maxSizeInMB],
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        showToast(validationError, 'failure');
        return;
      }
      onFileSelect(file);
      setIsFocused(false);
    },
    [validateFile, onFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect],
  );

  const removeFile = useCallback(() => {
    onFileSelect(null);
    setIsFocused(false);
  }, [onFileSelect]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }, []);

  const showError = useMemo(() => error && touched, [error, touched]);

  const content = selectedFile ? (
    <div className="flex flex-col relative items-center justify-center grow self-stretch gap-2">
      {/* Cross icon on top left */}
      <button onClick={removeFile} className="absolute top-2 right-2 size-6 rounded-xl bg-grey items-center justify-center cursor-pointer">
        <Text className="text-white text-base font-bold leading-[16px]">×</Text>
      </button>

      {/* File name */}
      <Text variant="body" className="cursor-pointer inline-flex gap-1 max-w-full truncate px-2">
        {selectedFile.name}
      </Text>

      {/* File size */}
      <Text className="text-grey text-sm">{formatFileSize(selectedFile.size)}</Text>
    </div>
  ) : (
    <>
      <Text className={cn('cursor-pointer inline-flex gap-1', showError && 'text-rejection')} variant="body">
        {label} {required && <span className="text-rejection">*</span>}
      </Text>
      {placeholder && <Text className="text-ui_grey text-center text-xs">{placeholder}</Text>}

      <input
        tabIndex={0}
        type="file"
        accept={acceptedTypes.map(type => `.${type}`).join(',')}
        onChange={handleFileInputChange}
        className="absolute left-0 top-0 opacity-0 w-full h-full cursor-pointer"
        onFocus={() => setIsFocused(true)}
        onBlur={(e: any) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />
    </>
  );

  return (
    <button
      tabIndex={-1}
      className={cn(
        'relative gap-1 border border-dashed border-border rounded-md h-24 p-1 flex flex-col items-center justify-center active:opacity-10 transition-opacity duration-250 ease-out overflow-hidden',
        isFocused && 'border-black border-2',
        showError && 'border-rejection border-2',
        dragOver && 'bg-ui_blue_medium/5',
        className,
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}>
      {content}

      {showError && (
        <Text variant="small" className="text-rejection text-center">
          {error}
        </Text>
      )}
    </button>
  );
};
